/**
 * Canonical RCL day ids + church-year math — the shared vocabulary behind
 * `LectionaryOccasion.dayId`. Ported from the Great Host of Composers build
 * (2026-07-05), where it was validated against their full 17-tab dataset.
 *
 * Two responsibilities:
 *  1. `canonicalDayId()` — normalize the many ways church musicians write a
 *     liturgical day ("Advent 1, Year A", "PROPER 9A", "Third Sunday in Lent")
 *     to one stable id ('advent-1', 'proper-9', 'lent-3').
 *  2. `rclDayForDate()` — compute the RCL Sunday (id + display + cycle year)
 *     for a calendar date, via computus. No reference data needed.
 *
 * Pure logic, no I/O — safe to import anywhere (Lambda, browser, scripts).
 */
const ORDINALS = {
    first: 1,
    second: 2,
    third: 3,
    fourth: 4,
    fifth: 5,
    sixth: 6,
    seventh: 7,
};
/**
 * `last` is deliberately ABSENT from ORDINALS.
 *
 * It used to map to 9, which turned "Last Sunday after Epiphany" into
 * `epiphany-9` — a numbered Sunday that does not exist in any lectionary, and
 * one that sorts among the numbered Epiphany Sundays instead of at the end of
 * the season. Worse, it was one of FOUR ids the same day could land on
 * depending on phrasing (`epiphany-9`, `last-sunday-after-the-epiphany`,
 * `last-epiphany`, `transfiguration`), so a catalogue accumulated a separate
 * day per spelling. Found in a client catalogue holding two of them at once.
 *
 * The Last Sunday after the Epiphany now resolves to the id the verified
 * lectionarypage.net tables use for it (`page-map.ts`). Any other "last Sunday
 * after X" slugifies literally, which is at least stable and honest rather than
 * a fabricated number.
 */
const LAST_SUNDAY_AFTER_EPIPHANY = 'last-sunday-after-epiphany';
function slugify(s) {
    return s
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
}
/**
 * Normalize a liturgical-day label to its canonical id. 'Advent 1, Year A',
 * 'Advent 1C', and 'First Sunday in Advent' all → 'advent-1'.
 */
export function canonicalDayId(day) {
    let d = day.replace(/\s+/g, ' ').trim();
    d = d.replace(/[,/]?\s*year\s*[abc]\b/gi, '');
    // Parentheticals (dates, notes). The close paren is OPTIONAL on purpose: a
    // director who types "Corpus Christi (Thursday after Trinity Sunday" and
    // forgets the bracket used to keep the whole aside in the slug, minting a
    // second day alongside `corpus-christi` that nothing could reconcile. An
    // unclosed parenthesis is an aside that ran to the end of the field.
    d = d.replace(/\([^)]*\)?/g, '');
    // "&" and "and" are the same word. Slugify drops "&" as punctuation, so
    // "St. Peter & St. Paul" and "St. Peter and St. Paul" produced two different
    // ids for one feast — the exact pair a client asked us to combine.
    d = d.replace(/\s*&\s*/g, ' and ');
    d = d.replace(/\b(proper\s*\d+)\s*[abc]\b/gi, '$1'); // Proper 26C → Proper 26
    d = d.replace(/\b(advent|lent|easter|epiphany|christmas)\s*(\d+)\s*[abc]\b/gi, '$1 $2');
    // The Last Sunday after the Epiphany, however it is written. Handled before
    // the ordinal rule because it has a NAME in the verified tables rather than a
    // number — see LAST_SUNDAY_AFTER_EPIPHANY.
    if (/^(?:the\s+)?last\s+sunday\s+(?:in|of|after)\s+(?:the\s+)?epiphany$/i.test(d.trim())) {
        return LAST_SUNDAY_AFTER_EPIPHANY;
    }
    const m = d.match(/^(first|second|third|fourth|fifth|sixth|seventh)\s+sunday\s+(?:in|of|after)\s+(?:the\s+)?(advent|lent|easter|epiphany|christmas|pentecost)\b/i);
    if (m)
        d = `${m[2]} ${ORDINALS[m[1].toLowerCase()]}`;
    return slugify(d);
}
/** Season display groups in church-year order. */
export const SEASONS = [
    'Advent',
    'Christmas',
    'Epiphany',
    'Lent',
    'Holy Week',
    'Easter',
    'Season after Pentecost',
];
const SEASON_PATTERNS = [
    [/advent/, 'Advent'],
    [/christmas|holy name/, 'Christmas'],
    [/epiphany|baptism|presentation|transfigur/, 'Epiphany'],
    [/ash wednesday|lent|annunciation/, 'Lent'],
    [/palm|passion|holy week|maundy|good friday|holy saturday/, 'Holy Week'],
    [/vigil|easter|ascension|pentecost$|day of pentecost/, 'Easter'],
];
/** Season for a canonical day id (defaults to Season after Pentecost). */
export function seasonForDayId(dayId) {
    const d = dayId.replace(/-/g, ' ');
    for (const [re, season] of SEASON_PATTERNS) {
        if (re.test(d))
            return season;
    }
    return 'Season after Pentecost';
}
const DAY_ORDER = [
    [/advent lessons/, 5],
    [/advent (\d)/, (m) => Number(m[1])],
    [/christmas lessons/, 10],
    [/christmas eve/, 11],
    [/christmas day|christmas$/, 12],
    [/christmas (\d)/, (m) => 12 + Number(m[1])],
    [/holy name|new year/, 15],
    // BEFORE the bare /epiphany$/ rule, not after it: "last-epiphany" ends in
    // "epiphany" and so matched that rule first, ranking the Last Sunday after
    // the Epiphany at 16 and sorting it ahead of The Epiphany itself. First match
    // wins, so the more specific pattern has to come first.
    [/transfigur|last.*epiphany/, 28],
    [/epiphany$|the epiphany/, 16],
    [/baptism/, 17],
    [/epiphany (\d)/, (m) => 17 + Number(m[1])],
    [/presentation/, 27],
    [/ash wednesday/, 30],
    [/lent (\d)/, (m) => 30 + Number(m[1])],
    [/annunciation/, 36],
    [/palm|passion sunday/, 37],
    [/holy week|holy monday|holy tuesday|holy wednesday/, 38],
    [/maundy/, 39],
    [/good friday/, 40],
    [/holy saturday/, 41],
    [/vigil/, 42],
    [/easter day|easter$/, 43],
    [/easter (\d)/, (m) => 43 + Number(m[1])],
    [/ascension/, 51],
    [/pentecost$|day of pentecost/, 53],
    [/trinity/, 54],
    [/proper (\d+)/, (m) => 55 + Number(m[1])],
    [/all saints/, 91],
    [/all souls/, 92],
    [/thanksgiving/, 93],
    [/reign of christ|christ the king/, 94],
];
/**
 * Church-year sort rank for a canonical day id (Advent 1 → Reign of Christ).
 * Unranked ids (saints' days, occasional services) return 99 — group them as
 * "Feasts & Commemorations" after the seasonal cycle.
 */
export function dayOrder(dayId) {
    const d = dayId.replace(/-/g, ' ');
    for (const [re, rank] of DAY_ORDER) {
        const m = d.match(re);
        if (m)
            return typeof rank === 'function' ? rank(m) : rank;
    }
    return 99;
}
// ---------------------------------------------------------------------------
// Church-year math
// ---------------------------------------------------------------------------
/** Gregorian computus (Meeus/Jones/Butcher) — Easter Sunday for a year. */
export function easterDate(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(Date.UTC(year, month - 1, day));
}
const DAY_MS = 86_400_000;
function addDays(d, n) {
    return new Date(d.getTime() + n * DAY_MS);
}
function sundayOnOrBefore(d) {
    return addDays(d, -d.getUTCDay());
}
function sundayClosestTo(d) {
    const before = sundayOnOrBefore(d);
    const after = addDays(before, 7);
    return d.getTime() - before.getTime() <= after.getTime() - d.getTime() ? before : after;
}
/** First Sunday of Advent for the liturgical year beginning in calendar year y. */
export function advent1(year) {
    const christmas = new Date(Date.UTC(year, 11, 25));
    let s = sundayOnOrBefore(christmas);
    if (s.getTime() === christmas.getTime())
        s = addDays(s, -7);
    return addDays(s, -21);
}
/** RCL cycle year (A/B/C) for a date. Year A begins Advent of years ≡ 0 mod 3. */
export function rclYear(date) {
    const y = date.getUTCFullYear();
    const start = advent1(y);
    const litYearStart = date.getTime() >= start.getTime() ? y : y - 1;
    return 'ABC'[litYearStart % 3];
}
/**
 * The RCL principal day for a SUNDAY date (UTC). Returns null for non-Sundays
 * and for the rare Sunday with no assignment. Trinity Sunday overrides its
 * Proper slot; Propers anchor to Sunday-closest-to fixed dates (Proper 4 ≈
 * June 1 … Proper 29 ≈ Nov 23, Christ the King).
 */
export function rclDayForDate(date) {
    if (date.getUTCDay() !== 0)
        return null;
    const y = date.getUTCFullYear();
    const t = date.getTime();
    const a1This = advent1(y);
    const e = easterDate(y);
    const ash = addDays(e, -46);
    const pentecost = addDays(e, 49);
    const year = rclYear(date);
    const out = (dayId, display) => ({ dayId, display, year });
    if (t >= a1This.getTime()) {
        const n = Math.floor((t - a1This.getTime()) / (7 * DAY_MS)) + 1;
        return n <= 4 ? out(`advent-${n}`, `Advent ${n}`) : out('christmas-1', 'First Sunday after Christmas');
    }
    if (t < Date.UTC(y, 0, 6))
        return out('christmas-2', 'Second Sunday after Christmas');
    if (t < ash.getTime()) {
        if (ash.getTime() - t <= 7 * DAY_MS)
            return out('transfiguration', 'Last Sunday after the Epiphany');
        const n = Math.floor((t - Date.UTC(y, 0, 6)) / (7 * DAY_MS)) + 1;
        if (n === 1)
            return out('baptism-of-christ', 'Baptism of Christ');
        return out(`epiphany-${n}`, `Epiphany ${n}`);
    }
    if (t < e.getTime()) {
        const n = Math.floor((t - ash.getTime()) / (7 * DAY_MS)) + 1;
        return n <= 5 ? out(`lent-${n}`, `Lent ${n}`) : out('palm-sunday', 'Palm Sunday');
    }
    if (t === e.getTime())
        return out('easter-day', 'Easter Day');
    if (t < pentecost.getTime()) {
        const n = Math.floor((t - e.getTime()) / (7 * DAY_MS)) + 1;
        return out(`easter-${n}`, `Easter ${n}`);
    }
    if (t === pentecost.getTime())
        return out('day-of-pentecost', 'Day of Pentecost');
    if (t === addDays(pentecost, 7).getTime())
        return out('trinity-sunday', 'Trinity Sunday');
    for (let p = 4; p <= 29; p++) {
        const anchor = addDays(new Date(Date.UTC(y, 4, 25)), 7 * (p - 3));
        if (sundayClosestTo(anchor).getTime() === t)
            return out(`proper-${p}`, `Proper ${p}`);
    }
    return null;
}
/** The next Sunday's RCL day on/after a date — "coming up this Sunday". */
export function upcomingRclDay(from) {
    let d = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), from.getUTCDate()));
    d = addDays(d, (7 - d.getUTCDay()) % 7);
    for (let i = 0; i < 8; i++) {
        const day = rclDayForDate(d);
        if (day)
            return { ...day, date: d.toISOString().slice(0, 10) };
        d = addDays(d, 7);
    }
    // Unreachable in practice — every Sunday resolves within a week or two.
    return { dayId: '', display: '', year: rclYear(d), date: d.toISOString().slice(0, 10) };
}
